import { Box, IconButton, Theme, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import {
    COLUMN_LABELS,
    DATE_FORMAT,
    FIELD_LABELS,
    formatDateWithRelative,
    formatFloat,
    shortLabel,
    useNumber,
} from '../../../../../../helpers';
import {
  ColumnOrder,
  TransfersPageQuery,
  TransfersSortableFields,
  useTransfersPageLazyQuery
} from '../../../../../../services';
import {
  AccountScope,
  CMinifiedTable,
  COwnAccountsBalances,
  CTableWrapper,
  DateRange,
  DBlock,
  DDateRangePicker,
  DeleteIcon,
  DIdAutocomplete,
  DPaginator,
  DTextField,
  EditIcon,
  selectPaginated,
  SortedOrder,
  TransferTypeScope,
  usePaginatedListBlock
} from '../../../../../dum';
import {
  SDeleteTransferDialog,
  SUpsertTransferDialog
} from '../../../../../smart';

// ── Transfer type constants ──────────────────────────────────────────────────
const TRANSFER_TYPE_OWN = 1;
const TRANSFER_TYPE_COBRANZA = 2;
const TRANSFER_TYPE_PAGO = 3;
const TRANSFER_TYPE_AJUSTE = 4;

type TransferDoc = TransfersPageQuery['paginatedTransfers']['docs'][number];
type ChipColor = 'success' | 'error' | 'primary' | 'default';

function getTypeColor(typeId: number | null | undefined): ChipColor {
    switch (typeId) {
        case TRANSFER_TYPE_COBRANZA: return 'success';
        case TRANSFER_TYPE_PAGO:     return 'error';
        case TRANSFER_TYPE_OWN:      return 'primary';
        case TRANSFER_TYPE_AJUSTE:
        default:                     return 'default';
    }
}

// Plain text color (no background tint) — used to color the amount and type label.
function getAmountSx(color: ChipColor) {
    if (color === 'default') {
        return { color: 'text.primary', fontWeight: 600 };
    }
    return {
        color: (theme: Theme) => theme.palette[color].main,
        fontWeight: 600,
    };
}

// First five characters of the counterpart own-account name, with the
// "Inopack" prefix stripped and the result trimmed — e.g. "Inopack Caucel"
// → "Cauce". Empty when there's no counterpart.
const shortCounterpart = (name?: string | null) =>
    (name ?? '').replace(/inopack/gi, '').trim().slice(0, 5);

// Memoized so it doesn't re-render when unrelated state changes.
const AccountCell = React.memo(function AccountCell({ transfer }: { transfer: TransferDoc }) {
    const typeId = transfer.transfer_type_id;

    if (typeId === TRANSFER_TYPE_OWN) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2">{transfer.from_account?.name ?? '—'}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>→</Typography>
                <Typography variant="body2">{transfer.to_account?.name ?? '—'}</Typography>
            </Box>
        );
    }
    if (typeId === TRANSFER_TYPE_COBRANZA) {
        // Counterpart is the own account that received the collection.
        const other = shortCounterpart(transfer.to_account?.name);
        return (
            <Typography variant="body2">
                {transfer.from_account?.name ?? '—'}
                {other ? ` (${other})` : ''}
            </Typography>
        );
    }
    if (typeId === TRANSFER_TYPE_PAGO) {
        // Counterpart is the own account the payment came out of.
        const other = shortCounterpart(transfer.from_account?.name);
        return (
            <Typography variant="body2">
                {transfer.to_account?.name ?? '—'}
                {other ? ` (${other})` : ''}
            </Typography>
        );
    }
    const label = transfer.from_account?.name ?? transfer.to_account?.name ?? '—';
    return <Typography variant="body2">{label}</Typography>;
});

// ── Block interface ───────────────────────────────────────────────────────────

export interface ITransfersListBlock {
    // When set, embeds the block scoped to an account or a transfer type.
    // Undefined renders the full standalone Transferencias page.
    scope?: AccountScope | TransferTypeScope;
    // Isolates filter/paginator localStorage state. Pass a unique key when
    // embedding so state doesn't collide with the standalone page.
    storageKey?: string;
    // Title shown in the CMinifiedTable header (embedded mode only).
    // Defaults to 'Transferencias'.
    title?: string;
}

export function TransfersListBlock({
    scope,
    storageKey,
    title,
}: ITransfersListBlock = {}) {
    const KEY = storageKey ?? TransfersListBlock.name;
    const embedded = !!scope;

    const [query, { data, previousData, loading }] = useTransfersPageLazyQuery();

    // Extra column-filter state — used in full mode only.
    const [accountIdState, setAccountId] = useNumber({ KEY: KEY + '_AccountId' });
    const [transferTypeIdState, setTransferTypeId] = useNumber({ KEY: KEY + '_TransferTypeId' });

    // Sort state — full mode only (column header clicks).
    const [sortedIndex, setSortedIndex] = useState<number | null>(null);
    const [sortedOrder, setSortedOrder] = useState<SortedOrder>(null);

    // Shared filter / paginator / dialog state from the hook.
    const {
        filter,
        setFilter,
        debouncedFilter,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        skip,
        take,
        setSkip,
        selectedId: transferId,
        upsertOpen,
        setUpsertOpen,
        deleteOpen,
        setDeleteOpen,
        openEdit,
        openDelete,
    } = usePaginatedListBlock({
        storageKey: KEY,
        embedded,
        resetDeps: [accountIdState, transferTypeIdState],
    });

    // Scope-level constraints override the column filters in embedded mode.
    const scopeAccountId      = scope?.type === 'account'      ? scope.id : null;
    const scopeTransferTypeId = scope?.type === 'transferType' ? scope.id : null;
    const accountId      = embedded ? scopeAccountId      : accountIdState;
    const transferTypeId = embedded ? scopeTransferTypeId : transferTypeIdState;

    useEffect(() => {
        void query({
            variables: {
                skip,
                take,
                // Embedded mode renders no date picker, so a persisted date range
                // would be an invisible, un-clearable filter (and an equal
                // start/end silently returns zero rows on the backend). Force the
                // dates to null when scoped — same as accountId/transferTypeId/sort
                // below, the scope owns the query and ignores the saved filters.
                startDate: embedded ? null : startDate || null,
                endDate: embedded ? null : endDate || null,
                accountId,
                transferTypeId,
                // Embedded views are operational (dashboard), so they hide
                // transfers that only settle reconciliation-only documents.
                // The full Transferencias page keeps every transfer.
                excludeReconciliationOnly: embedded,
                filter: debouncedFilter || '',
                // Embedded: always sort date desc. Full: follow sort state.
                sortOrder: embedded
                    ? ColumnOrder.Desc
                    : sortedOrder === 'asc'  ? ColumnOrder.Asc
                    : sortedOrder === 'desc' ? ColumnOrder.Desc
                    : null,
                sortField: embedded
                    ? TransfersSortableFields.TransferredDate
                    : sortedIndex === 0 ? TransfersSortableFields.TransferredDate : null,
            },
        });
    }, [query, skip, take, startDate, endDate, debouncedFilter, sortedOrder, sortedIndex, accountId, transferTypeId, embedded]);

    const { docs: transfers, count } = selectPaginated(
        data?.paginatedTransfers,
        previousData?.paginatedTransfers,
    );

    const handleEditTransfer = useCallback(
        (transfer: TransferDoc) => openEdit(transfer.id),
        [openEdit],
    );
    const handleDeleteTransfer = useCallback(
        (transfer: TransferDoc) => openDelete(transfer.id),
        [openDelete],
    );

    const dialogs = (
        <>
            <SUpsertTransferDialog open={upsertOpen} setOpen={setUpsertOpen} transferId={transferId} />
            <SDeleteTransferDialog open={deleteOpen} setOpen={setDeleteOpen} transferId={transferId} />
        </>
    );

    // ── Embedded (scope) mode ─────────────────────────────────────────────────
    // CMinifiedTable: single general filter + paginator, compact columns.
    if (embedded) {
        // Everything the embedded query applies, in plain words — the minified
        // table has no column filters, so these would otherwise be invisible.
        const scopeLabel = scope?.type === 'transferType'
            ? `Tipo: ${data?.getTransferTypes.find((t) => t.id === scope.id)?.name ?? scope.id}`
            : `Cuenta: ${data?.getAccounts.find((a) => a.id === scope?.id)?.name ?? scope?.id}`;
        const appliedFilters = [
            scopeLabel,
            'Sin registros solo de conciliación',
            ...(debouncedFilter ? [`Búsqueda: "${debouncedFilter}"`] : []),
            'Orden: fecha de transferencia, más reciente primero',
        ];

        return (
            <>
                <CMinifiedTable
                    title={title ?? 'Transferencias'}
                    appliedFilters={appliedFilters}
                    loading={loading}
                    filterValue={filter}
                    onFilterChange={(value) => setFilter(value || '')}
                    filterLabel={'Buscar'}
                    filterHelperText={'Cuenta, folio o notas'}
                    skip={skip}
                    take={take}
                    setSkip={setSkip}
                    count={count}
                    onRowDoubleClick={handleEditTransfer}
                    items={[
                        {
                            title: shortLabel(FIELD_LABELS.transferred_date),
                            textAlign: 'center',
                            content: (t) => formatDateWithRelative(t.transferred_date),
                        },
                        {
                            title: shortLabel(COLUMN_LABELS.transfer_accounts),
                            content: (t) => <AccountCell transfer={t} />,
                        },
                        {
                            title: shortLabel(FIELD_LABELS.amount),
                            textAlign: 'right',
                            content: (t) => formatFloat(t.amount),
                        },
                    ]}
                    data={transfers}
                    contextItems={[
                        { title: shortLabel(COLUMN_LABELS.edit), callback: handleEditTransfer },
                        { title: shortLabel(COLUMN_LABELS.delete), callback: handleDeleteTransfer },
                    ]}
                />
                {dialogs}
            </>
        );
    }

    return (
        <DBlock>
            {/* ── Own-account balance tiles ─────────────────────────── */}
            <COwnAccountsBalances
                loading={loading && !data}
                accounts={data?.getOwnAccountsTransferSummary ?? []}
                sx={{ mb: 2.5 }}
            />

            {/* ── Title + filter row + paginator ────────────────────── */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Transferencias</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <DTextField
                        label={'Buscar'}
                        helperText={'Cuenta, folio o notas'}
                        value={filter ?? ''}
                        onChange={(val) => setFilter(val)}
                        hideHelperTextBelowLg
                    />
                </Box>
                <DPaginator skip={skip} setSkip={setSkip} take={take} count={count} disableGutters />
            </Box>

            {/* ── Table: normal columns, no left panel ─────────────── */}
            <CTableWrapper
                onRowDoubleClick={handleEditTransfer}
                contextItems={[
                    { title: shortLabel(COLUMN_LABELS.edit), callback: handleEditTransfer },
                    { title: shortLabel(COLUMN_LABELS.delete), callback: handleDeleteTransfer },
                ]}
                sortedIndex={sortedIndex}
                sortedOrder={sortedOrder}
                setSortedIndex={setSortedIndex}
                setSortedOrder={setSortedOrder}
                items={[
                    {
                        title: shortLabel(FIELD_LABELS.transferred_date),
                        cellGrow: 12,
                        sortable: true,
                        textAlign: 'center',
                        filter: (
                            <Box sx={{ width: '100%', '& .MuiTextField-root': { minWidth: 0 } }}>
                                <DDateRangePicker
                                    label={'Fecha'}
                                    value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : undefined}
                                    onChange={(range: DateRange) => {
                                        setStartDate(range[0].format(DATE_FORMAT));
                                        setEndDate(range[1].format(DATE_FORMAT));
                                    }}
                                    onClear={() => { setStartDate(''); setEndDate(''); }}
                                    disableGutters
                                />
                            </Box>
                        ),
                        content: (t) => formatDateWithRelative(t.transferred_date),
                    },
                    {
                        title: shortLabel(COLUMN_LABELS.transfer_accounts),
                        cellGrow: 18,
                        filter: (
                            <Box sx={{ width: '100%', '& .MuiTextField-root': { minWidth: 0 } }}>
                                <DIdAutocomplete
                                    id={accountIdState}
                                    onChange={setAccountId}
                                    itemText={'compound_name'}
                                    label={'Cuenta'}
                                    options={data?.getAccounts || []}
                                    disableGutters
                                />
                            </Box>
                        ),
                        content: (t) => <AccountCell transfer={t} />,
                    },
                    {
                        title: shortLabel(FIELD_LABELS.transfer_type_id),
                        cellGrow: 12,
                        textAlign: 'left',
                        filter: (
                            <Box sx={{ width: '100%', '& .MuiTextField-root': { minWidth: 0 } }}>
                                <DIdAutocomplete
                                    id={transferTypeIdState}
                                    onChange={setTransferTypeId}
                                    itemText={'name'}
                                    label={'Tipo'}
                                    options={data?.getTransferTypes || []}
                                    disableGutters
                                />
                            </Box>
                        ),
                        content: (t) => (
                            <Typography variant="body2" sx={getAmountSx(getTypeColor(t.transfer_type_id))}>
                                {t.transfer_type?.name ?? '—'}
                            </Typography>
                        ),
                    },
                    {
                        title: shortLabel(FIELD_LABELS.notes),
                        cellGrow: 16,
                        content: (t) => t.notes || '',
                    },
                    {
                        title: shortLabel(COLUMN_LABELS.transfer_folios),
                        cellGrow: 18,
                        content: (t) =>
                            t.transfer_receipts
                                .map((tr) => tr.expense?.external_code || tr.order_sale?.compound_order_code || '')
                                .filter(Boolean)
                                .join(', ') || '—',
                    },
                    {
                        title: shortLabel(FIELD_LABELS.amount),
                        cellGrow: 12,
                        textAlign: 'right',
                        content: (t) => {
                            const difference = t.amount - t.transfer_receipts_total;
                            const isOwnAccountTransfer = t.transfer_type_id === TRANSFER_TYPE_OWN;
                            return (
                                <Box>
                                    <Typography component={'span'} variant={'body2'} sx={getAmountSx(getTypeColor(t.transfer_type_id))}>
                                        {formatFloat(t.amount)}
                                    </Typography>
                                    {difference !== 0 && !isOwnAccountTransfer && (
                                        <Typography component={'span'} variant={'caption'} color={'warning.main'} sx={{ ml: 0.5, display: 'block' }}>
                                            (pendiente: {formatFloat(difference)})
                                        </Typography>
                                    )}
                                </Box>
                            );
                        },
                    },
                    {
                        title: shortLabel(FIELD_LABELS.updated_by_id),
                        cellGrow: 14,
                        content: (t) => t.updated_by?.fullname || '—',
                    },
                    {
                        title: '',
                        cellGrow: 10,
                        content: (t) => (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <IconButton
                                    size="small"
                                    sx={{ bgcolor: 'rgba(34,197,94,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                    onClick={() => handleEditTransfer(t)}
                                >
                                    <EditIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{ bgcolor: 'rgba(239,68,68,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                    onClick={() => handleDeleteTransfer(t)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ),
                    },
                ]}
                data={transfers}
                loading={loading}
            />

            {dialogs}
        </DBlock>
    );
}
